import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { WorkspaceStereo } from '@lemoncloud/codes-goods-api';
import { AlertDialogDescription } from '@radix-ui/react-alert-dialog';
import { DialogDescription } from '@radix-ui/react-dialog';
import { Check } from 'lucide-react';

import { useClouds } from '@codes/cloud';
import { useCards } from '@codes/payments';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Skeleton } from '@codes/ui-kit';

import { useDialogStore } from '../../stores';

export const PricingPlansDialog = () => {
    const { t } = useTranslation();
    const { isPriceDialogOpen, setPriceDialog } = useDialogStore(state => state);

    const { setWorkspaceDialog, setWorkspacePlan, setCardDialog, workspacePlan, setSelectedWorkspacePlan } =
        useDialogStore(state => state);

    const [alertOpen, setAlertOpen] = useState(false);

    const { data: cardsData, isLoading: isCardsFetching } = useCards({ stereo: 'stripe', view: 'mine' });

    const hasValidCard = cardsData?.list && (cardsData?.total || 0) > 0;

    const {
        data: cloudsData,
        isFetching: isFetchingClouds,
        isRefetching: isRefetchingClouds,
        error: cloudsFetchingError,
        refetch: refetchClouds,
    } = useClouds({});

    const openWorkspaceDialog = (isPro: boolean = false) => {
        if (isPro) {
            if (!hasValidCard) {
                setAlertOpen(true);
                return;
            }

            setSelectedWorkspacePlan('pro');
            setWorkspaceDialog(true);
            setPriceDialog(false);
            setWorkspacePlan(null);

            return;
        }

        setSelectedWorkspacePlan('basic');
        setWorkspaceDialog(true);
        setPriceDialog(false);
        setWorkspacePlan(null);
    };

    const enrollCard = () => {
        setCardDialog(true);
    };

    const resetData = () => {
        setWorkspacePlan(null);
    };

    const ContentView = () => {
        const isLoading = isFetchingClouds || isRefetchingClouds;
        if (isLoading) {
            return (
                <div className="px-[26px] py-6 h-[calc(100%-52px)] gap-y-2 overflow-auto flex flex-col max-xs:px-6">
                    <Skeleton className=" h-16" />
                    <div className="grid grid-cols-[repeat(3,minmax(292px,1fr))] gap-2 max-sm:grid-cols-1">
                        <Skeleton className="h-[394px]"></Skeleton>
                        <Skeleton className="h-[394px]"></Skeleton>
                        <Skeleton className="h-[394px]"></Skeleton>
                    </div>
                </div>
            );
        }

        if (cloudsFetchingError) {
            return (
                <div className="h-96 flex flex-col gap-y-2 items-center justify-center">
                    <div className="text-primary text-lg">Failed to fetch clouds</div>
                    <Button onClick={() => refetchClouds()}>Retry</Button>
                </div>
            );
        }

        return (
            <div className="px-[26px] py-6 h-[calc(100%-52px)] overflow-auto flex flex-col max-xs:px-6">
                <DialogDescription className="text-[28px] text-text font-semibold mb-6">
                    {t('subscription.upgradeToBasicPlan')}
                </DialogDescription>
                <ul className="grid grid-cols-[repeat(3,minmax(292px,1fr))] gap-2 max-sm:grid-cols-1">
                    {cloudsData?.list?.map(cloud => (
                        <li
                            key={cloud.id}
                            className={`bg-box h-[394px] py-[18px] px-[22px] relative rounded-[16px] shadow-custom border border-divider flex flex-col hover:border-point overflow-hidden ${
                                workspacePlan === cloud.planType ? 'border-point' : ''
                            }`}
                        >
                            <div
                                className={`absolute top-0 right-0 ${formatBadgeClass(
                                    cloud.planType
                                )}  px-10 py-[2px]  transform rotate-45 translate-x-10 -translate-y-9 origin-bottom-left`}
                            >
                                <div className="text-white text-base font-medium w-11 text-center">
                                    {formatBadgeName(cloud.planType)}
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="text-[24px] font-medium">{formatCloudName(cloud.planType)}</div>
                                <div className=" my-2 text-[32px] font-semibold">
                                    {formatCloudPrice(cloud.planType)}
                                    <span className="text-base font-medium ml-2">
                                        {formatCloudDescription(cloud.planType)}
                                    </span>
                                </div>
                                <div className="flex flex-col gap-y-1">
                                    {cloud.features?.map((feature, featureIdx) => (
                                        <div className="flex items-center gap-[7px]" key={featureIdx}>
                                            <Check className="min-w-4 min-h-4 w-4 h-4 text-point" />
                                            <span className="text-sm text-sub">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="w-full mt-auto">
                                <Button
                                    variant={workspacePlan === cloud.planType ? 'default' : 'outlinePoint'}
                                    className={`w-full max-w-full h-11 ${
                                        workspacePlan === cloud.planType ? '' : ' text-text'
                                    }   text-base border-text`}
                                    disabled={cloud.planType === 'ent'}
                                    onClick={() => openWorkspaceDialog(cloud.planType === 'pro')}
                                >
                                    {formatButtonText(cloud.planType)}
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const formatBadgeClass = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return 'bg-pro';
            case 'ent':
            case 'adv':
                return 'bg-adv';
            default:
                return 'bg-sub';
        }
    };

    const formatBadgeName = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return 'Pro';
            case 'ent':
            case 'adv':
                return 'Adv';
            case 'basic':
                return 'Basic';
            case 'free':
                return 'Free';
            default:
                return '-';
        }
    };

    const formatCloudName = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return 'Pro';
            case 'ent':
            case 'adv':
                return 'Advanced';
            case 'basic':
                return 'Basic';
            case 'free':
                return 'Free';
            default:
                return '-';
        }
    };

    const formatCloudPrice = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return '$20';
            case 'ent':
            case 'adv':
                return '$40';
            default:
                return '$0';
        }
    };

    const formatCloudDescription = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return 'per workspace/month';
            case 'ent':
            case 'adv':
                return 'per user/month';
            default:
                return '';
        }
    };

    const formatButtonText = (planType: WorkspaceStereo | undefined) => {
        switch (planType) {
            case 'pro':
                return t('subscription.startWithPro');
            case 'ent':
            case 'adv':
                return t('subscription.startWithAdv');
            default:
                return t('subscription.startWithBasic');
        }
    };

    useEffect(() => {
        if (!isPriceDialogOpen) {
            resetData();
        }
    }, [isPriceDialogOpen]);

    return (
        <Dialog open={isPriceDialogOpen} onOpenChange={setPriceDialog}>
            <DialogContent aria-describedby={undefined} className="!max-w-[944px] p-0">
                <div className="h-full">
                    <DialogHeader>
                        <DialogTitle className="text-base">{t('subscription.subscriptionManagement')}</DialogTitle>
                    </DialogHeader>
                    <ContentView />
                </div>
            </DialogContent>
            <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
                <AlertDialogContent>
                    <AlertDialogTitle>{t('serviceRequest.registerPaymentMethod')}</AlertDialogTitle>
                    <AlertDialogDescription className="text-center text-sub mt-2">
                        {t('serviceRequest.registerCardToProceed')}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('serviceRequest.close')}</AlertDialogCancel>
                        <AlertDialogAction onClick={enrollCard}>{t('serviceRequest.registerCard')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
};
